import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Location } from '@angular/common';

export enum NavigationContext {
  Gallery = 'gallery',
  GalleryManage = 'gallery-manage',
  Projects = 'projects',
  Unknown = 'unknown',
}

@Injectable({
  providedIn: 'root',
})
export class NavigationService {
  private readonly router = inject(Router);
  private readonly location = inject(Location);

  /**
   * Навигация назад на один уровень в URL
   * Удаляет последний сегмент из текущего URL
   * @param queryParams - дополнительные query параметры для передачи
   */
  back(queryParams?: Record<string, string | number | boolean | null | undefined>): void {
    const currentUrl = this.router.url;
    const segments = currentUrl.split('/').filter(s => s);

    if (segments.length > 0) {
      const parentUrl = '/' + segments.slice(0, -1).join('/');
      if (queryParams) {
        this.router.navigate([parentUrl], { queryParams });
      } else {
        this.router.navigateByUrl(parentUrl);
      }
    } else {
      // Если нет родительского URL, используем browser history
      this.location.back();
    }
  }

  /**
   * Навигация назад на указанное количество уровней
   * @param levels - количество уровней для возврата (по умолчанию 1)
   * @param queryParams - дополнительные query параметры для передачи
   */
  backLevels(levels: number = 1, queryParams?: Record<string, string | number | boolean | null | undefined>): void {
    const currentUrl = this.router.url;
    const segments = currentUrl.split('/').filter(s => s);

    if (segments.length >= levels) {
      const parentUrl = '/' + segments.slice(0, -levels).join('/');
      if (queryParams) {
        this.router.navigate([parentUrl], { queryParams });
      } else {
        this.router.navigateByUrl(parentUrl);
      }
    } else {
      // Если уровней недостаточно, используем browser history
      this.location.back();
    }
  }

  /**
   * Навигация назад на указанное количество уровней и затем к sibling пути
   * @param levels - количество уровней для возврата
   * @param siblingPath - путь к sibling маршруту (добавляется после возврата)
   * @param queryParams - дополнительные query параметры для передачи
   */
  backLevelsToSibling(
    levels: number,
    siblingPath: string,
    queryParams?: Record<string, string | number | boolean | null | undefined>,
  ): void {
    const currentUrl = this.router.url.split('?')[0];
    const segments = currentUrl.split('/').filter(s => s);

    if (segments.length >= levels) {
      const targetUrl = '/' + segments.slice(0, -levels).join('/') + '/' + siblingPath;
      if (queryParams) {
        this.router.navigate([targetUrl], { queryParams });
      } else {
        this.router.navigateByUrl(targetUrl);
      }
    } else {
      this.location.back();
    }
  }

  /**
   * Навигация к конкретному родительскому пути
   * @param parentPath - путь к родительскому маршруту
   * @param queryParams - дополнительные query параметры для передачи
   */
  backTo(parentPath: string, queryParams?: Record<string, string | number | boolean | null | undefined>): void {
    if (queryParams) {
      this.router.navigate([parentPath], { queryParams });
    } else {
      this.router.navigateByUrl(parentPath);
    }
  }

  /**
   * Определяет контекст навигации (gallery или projects)
   */
  getNavigationContext(): NavigationContext {
    const currentUrl = this.router.url;
    const segments = currentUrl.split('/').filter(s => s);

    if (segments.includes('gallery')) {
      return NavigationContext.Gallery;
    } else if (segments.includes('projects')) {
      return NavigationContext.Projects;
    } else if (segments.includes('gallery-manage')) {
      return NavigationContext.GalleryManage;
    }

    return NavigationContext.Unknown;
  }

  /**
   * Проверяет, можно ли вернуться назад
   */
  canGoBack(): boolean {
    const currentUrl = this.router.url;
    const segments = currentUrl.split('/').filter(s => s);
    return segments.length > 0;
  }

  /**
   * Получает родительский URL
   */
  getParentUrl(): string {
    const currentUrl = this.router.url;
    const segments = currentUrl.split('/').filter(s => s);

    if (segments.length > 0) {
      return '/' + segments.slice(0, -1).join('/');
    }

    return '/';
  }

  /**
   * Получает URL для списка фотокниг в текущем контексте
   */
  getPhotoBookListUrl(): string {
    const currentUrl = this.router.url;
    const segments = currentUrl.split('/').filter(s => s);

    const photoBookIndex = segments.findIndex(segment => segment === 'photo-books');

    if (photoBookIndex !== -1) {
      return '/' + segments.slice(0, photoBookIndex + 1).join('/');
    }

    return this.getParentUrl();
  }
}
